// janus-engine/economy/out-of-combat.ts — R-124 (Janus lane N1, 2026-10-01): the OUT-OF-COMBAT TURN ECONOMY as a pure browser rule.
//
// The port of sync_out_of_combat_encounter_creature_turn_economy() (includes/functions.php) + pin_summoner_ether_to_max_out_of_combat()
// (janus/encounter/pin_summoner_ether_to_max_out_of_combat.php). Owner 2026-09-30: "This should be front end code scoped to the
// encounter. I don't care about statuses outside of the encounter for now either if a player is not there or an active participant
// isn't there." So it runs ONLY over the rows the encounter page holds, and only while that held encounter is out of combat:
//   1. Ether pin — an ACTIVE `passive_ability` entry of ClassMechanic 'ether' (Summoner) whose pool is not exactly its numeric max > 0
//      is set to that max (a non-numeric / non-positive max = no ceiling to pin to; already exactly full = nothing).
//      Psi pin (owner 2026-10-16) — the Psion's Psi follows the SAME out-of-combat rule: it reads full whenever the encounter is out of
//      combat (the psion is assumed to be channelling between fights — no heartbeat accrual to write), an out-of-combat Psi spend is
//      skipped (ability/charges/adjust-charges.ts), and every combat STARTS it at 0 (combat/start-plan.ts) so it is built up in the fight.
//   2. Blocking flags — `no_actions` / `no_reactions` = any PAYLOAD status (creature_payload_statuses: target rows minus hostile-aura
//      self-anchors and dormant owner-gated auras) that is not campaign-paused, not `status_private`, whose definition carries the flag
//      truthy (PHP `!empty(layer_value($def, $flag))`).
//   3. AP — unless `no_actions`: aps → max(0, aps_total ?? 3) (the patcher twin clears aps_used).
//   4. Reaction — unless `no_reactions`: reaction → 1.
// Deliberate differences from the PHP (logged in the lane handoff, Decisions taken):
//   · a DEAD row is skipped (nothing to rest; the legacy sync rested corpses on every hydrate);
//   · a payload status whose definition this page does not hold is UNKNOWN, never "not blocking": the actor's AP / reaction rest is
//     skipped and the definition id reported in `missing` so the adapter can fetch it and plan again (PHP had the compiled definition in
//     hand). The Ether pin is NOT held back — it never reads statuses (the PHP pins Ether before it even looks at the blocking flags);
//   · in combat — or a held encounter whose combat state cannot be read — plans nothing (PHP's unknown → no write, same direction).
// The adapter (websocket/ooc-rest-truth.ts) saves aps / reaction through the L3 resource lane (`ooc_rest`) and the Ether pin through the
// charge lane; PHP authorizes from the target row and saves.
import { isDormantOwnerGatedAura, isHostileAuraSelfAnchor, readCreatureStatuses, AuraOwnerGateUnavailable } from '../compile/creature-status-reader.js';
import type { ItemFieldRow } from '../compile/item-field-reader.js';
import { phpJsonTruthy } from '../compile/php-json-values.js';
import { heldLayerlessDefinition } from '../compile/held-definition.js';

/** ClassMechanic 'ether' (includes/additions.php, Summoner). */
export const ETHER_MECHANIC_ID = '7A725B38-BA68-7651-4746-5D34B5690203';
/** ClassMechanic 'psi' (includes/additions.php, Psion) — the passive_ability Psi pool. */
export const PSI_MECHANIC_ID = '6A8183E1-349E-3960-B465-6C863097E193';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
/** PHP `(int)` of a stored scalar: null / '' / absent → 0. */
const int = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
/** PHP is_numeric() for a compiled scalar. */
const phpNumeric = (value: unknown): boolean => typeof value === 'number' ? Number.isFinite(value)
  : typeof value === 'string' && /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(value);

export interface OocEconomyReaders {
  /** layer_value() twin (ftLayerValue). */
  layerValue: (row: Row | null, key: string) => unknown;
  /** layer_entries() twin (ftLayerEntries). */
  entries: (row: Row | null, key: string) => unknown[];
  /** A catalog definition the page holds (status definitions, the Ether definition's `ability_charges`), or null. */
  definition: (id: string) => Row | null;
}

export interface OocRestOp {
  actorId: string;
  /** aps / reaction before → after (both columns always named; an unchanged one carries its own value). Absent = no column change. */
  values?: { before: { aps: number; reaction: number }; after: { aps: number; reaction: number } };
  /** The Ether pin: the pool's current count and the max it is pinned to. */
  ether?: PoolPin;
  /** The Psi pin (same rule as Ether, owner 2026-10-16). */
  psi?: PoolPin;
}
export interface PoolPin { itemId: string; before: number; after: number }
export interface OocRestPlan {
  /** 'in-combat' / 'combat-unknown' plan nothing. */
  state: 'out-of-combat' | 'in-combat' | 'combat-unknown';
  ops: OocRestOp[];
  /** Definition ids the page must fetch before it can judge an actor (each such actor is in `skipped`). */
  missing: string[];
  skipped: { actorId: string; reason: string }[];
}

/** The held encounter's combat flag: a compiled blob omits in_combat when it is 0 (the bucket is present, the key is not). */
export function encounterInCombat(encounter: Row | null): boolean | null {
  if (!encounter) return null;
  const bucket = asRow(encounter.encounters);
  const raw = scalar(bucket && 'in_combat' in bucket ? bucket.in_combat : encounter.in_combat);
  if (raw === undefined || raw === null || raw === '') return bucket ? false : null;
  return Number(raw) === 1;
}

/** status_instance_has_campaign_pause(): any status_paused_campaign_* component set to a non-zero value. */
function campaignPaused(status: Row): boolean {
  return ['year', 'month', 'day', 'hour', 'minute', 'second'].some(part => {
    const v = scalar(status[`status_paused_campaign_${part}`]);
    if (v === null || v === undefined || v === '') return false;
    return !(phpNumeric(v) && Math.trunc(Number(v)) === 0);
  });
}
/** A definition the reader could not resolve: readCreatureStatuses leaves the identity shell {items:{item_id}} with no layers.
 *  A layer-less status's lean catalog row is held, not a shell (heldLayerlessDefinition). */
const isShell = (def: Row): boolean => !('item_layer_types' in def) && !('computed' in def) && !heldLayerlessDefinition(def);

/** creature_item_arr_has_blocking_status_flag() over BOTH flags at once, on the payload statuses. `unknown` = a definition not held. */
export function blockingFlags(row: Row, roster: Row[], readers: OocEconomyReaders, catalog: Row | null = null):
  { noActions: boolean; noReactions: boolean; unknown: string[] } {
  const out = { noActions: false, noReactions: false, unknown: [] as string[] };
  const layerValue = (r: ItemFieldRow | null, key: string): unknown => readers.layerValue(r as Row | null, key);
  const resolve = (id: string): ItemFieldRow | null => {
    const hit = asRow(catalog?.[id] ?? catalog?.[id.toUpperCase()] ?? catalog?.[id.toLowerCase()]);
    const def = hit && asRow(hit.items) && ('item_layer_types' in hit || 'computed' in hit || heldLayerlessDefinition(hit)) ? hit : readers.definition(id);
    return def as ItemFieldRow | null;
  };
  // ft_aura_owner_state(): the owner as THIS page holds it — absent from the roster = unresolvable = dormant (PHP returns true there).
  const owner = (id: string): ItemFieldRow | null => (roster.find(r => upper(asRow(r.encounter_creatures)?.encounter_creature_id) === id.toUpperCase()) ?? null) as ItemFieldRow | null;
  const statuses = readCreatureStatuses(row as ItemFieldRow, 'target', resolve) as unknown;
  for (const value of statuses !== null && typeof statuses === 'object' ? Object.values(statuses) : []) {
    const status = asRow(value);
    if (!status) continue;
    if (Number(scalar(status.status_deleted) ?? 0) !== 0) continue;
    const def = asRow(status.status_arr);
    if (!def) continue;
    // creature_payload_statuses(): the hostile-aura self-anchor and a dormant owner-gated aura deliver no payload to the holder.
    if (isHostileAuraSelfAnchor(def, status.status_aoe_source, status.status_caster_id, status.status_target_id, layerValue)) continue;
    let dormant: boolean;
    try { dormant = isDormantOwnerGatedAura(status as ItemFieldRow, layerValue, owner, row as ItemFieldRow); }
    catch (error) { if (error instanceof AuraOwnerGateUnavailable) { out.unknown.push('aura-owner:' + upper(status.status_caster_id)); continue; } throw error; }
    if (dormant) continue;
    if (campaignPaused(status)) continue;
    if (phpJsonTruthy(scalar(status.status_private))) continue;
    if (isShell(def)) { out.unknown.push(upper(status.status_item_id ?? asRow(def.items)?.item_id)); continue; }
    if (phpJsonTruthy(readers.layerValue(def, 'no_actions'))) out.noActions = true;
    if (phpJsonTruthy(readers.layerValue(def, 'no_reactions'))) out.noReactions = true;
  }
  return out;
}

/** ability_entry_id(): the flat ability_id, a bare-UUID ref, the nested item_arr / entry item_id. */
function entryId(value: unknown): string {
  if (typeof value === 'string') return upper(value);
  const entry = asRow(value);
  if (!entry) return '';
  const nested = entry.item_arr;
  const nestedId = typeof nested === 'string' ? nested : asRow(asRow(nested)?.items)?.item_id ?? asRow(nested)?.item_id;
  return upper(entry.ability_id ?? nestedId ?? asRow(entry.items)?.item_id ?? entry.item_id);
}

/** pin_summoner_ether_to_max_out_of_combat() decided on a held row: the pin, nothing, or `missing` (the Ether definition is not held). */
export function etherPin(row: Row, readers: OocEconomyReaders): PoolPin | 'missing' | null {
  return poolPin(row, readers, ETHER_MECHANIC_ID);
}
/** The Psion's Psi, pinned full out of combat by the same rule (owner 2026-10-16). */
export function psiPin(row: Row, readers: OocEconomyReaders): PoolPin | 'missing' | null {
  return poolPin(row, readers, PSI_MECHANIC_ID);
}
/** The active `passive_ability` Psi entry a row holds (combat/start-plan.ts zeroes it at the bell). A bare-UUID ref carries no stamp,
 *  so it is active (zeroing needs no compiled count — unlike the pin). */
export function holdsActivePsi(row: Row, readers: Pick<OocEconomyReaders, 'entries'>): boolean {
  return readers.entries(row, 'passive_ability').some(value => {
    if (entryId(value) !== PSI_MECHANIC_ID) return false;
    const entry = asRow(value);
    return !entry || !('passive_conditions_active' in entry) || phpJsonTruthy(scalar(entry.passive_conditions_active));
  });
}

/** A full-out-of-combat pool (Ether / Psi) on a held row: the pin to its max, nothing, or `missing` (its definition is not held). */
function poolPin(row: Row, readers: OocEconomyReaders, poolId: string): PoolPin | 'missing' | null {
  for (const value of readers.entries(row, 'passive_ability')) {
    const entry = asRow(value);
    // A bare-UUID ref carries no per-entry stamp (active) but also no compiled count: nothing this page can pin.
    if (!entry || entryId(entry) !== poolId) continue;
    // passive_ability_entry_is_active(): no stamp = active; a stamp is judged by truthiness ('' / null / false / 0 = inactive).
    if ('passive_conditions_active' in entry && !phpJsonTruthy(scalar(entry.passive_conditions_active))) continue;
    const charges = asRow(entry.charges);
    // ability_entry_max_charges($passive, 0): charges.max, the flat max, then the DEFINITION's ability_charges layer, else 0.
    let max: unknown = charges && 'max' in charges ? charges.max : 'max_ability_charges' in entry ? entry.max_ability_charges : undefined;
    let current: unknown = charges && 'current' in charges ? charges.current : 'ability_charges' in entry ? entry.ability_charges : undefined;
    if (max === undefined || current === undefined) {
      const def = readers.definition(poolId);
      if (!def) return 'missing';
      const layer = scalar(readers.layerValue(def, 'ability_charges'));
      if (max === undefined) max = layer ?? 0;
      if (current === undefined) current = layer ?? 0;
    }
    max = scalar(max); current = scalar(current);
    if (!phpNumeric(max) || Math.trunc(Number(max)) <= 0) return null; // no ceiling to pin to
    const ceiling = Math.trunc(Number(max));
    if (phpNumeric(current) && Math.trunc(Number(current)) === ceiling) return null; // already exactly full
    return { itemId: poolId, before: phpNumeric(current) ? Math.trunc(Number(current)) : 0, after: ceiling };
  }
  return null;
}

/**
 * The rest plan for the rows this page holds of ONE encounter. `rows` may be a subset of the roster (the adapter plans only rows its
 * viewer may save); `roster` is the whole held roster (the aura-owner gate reads owners from it).
 */
export function planOutOfCombatEconomy(encounter: Row | null, rows: Row[], readers: OocEconomyReaders, roster: Row[] = rows): OocRestPlan {
  const inCombat = encounterInCombat(encounter);
  if (inCombat !== false) return { state: inCombat === true ? 'in-combat' : 'combat-unknown', ops: [], missing: [], skipped: [] };
  const plan: OocRestPlan = { state: 'out-of-combat', ops: [], missing: [], skipped: [] };
  const catalog = asRow(encounter?.catalog);
  for (const row of rows) {
    const ec = asRow(row.encounter_creatures) ?? {};
    const actorId = upper(ec.encounter_creature_id);
    if (!actorId) continue;
    if (int(ec.encounter_creature_dead) !== 0 || int(ec.encounter_creature_deleted) !== 0) continue;
    const op: OocRestOp = { actorId };
    const ether = etherPin(row, readers);
    if (ether === 'missing') { plan.missing.push(ETHER_MECHANIC_ID); plan.skipped.push({ actorId, reason: 'ether-definition-not-held' }); }
    else if (ether) op.ether = ether;
    const psi = psiPin(row, readers);
    if (psi === 'missing') { plan.missing.push(PSI_MECHANIC_ID); plan.skipped.push({ actorId, reason: 'psi-definition-not-held' }); }
    else if (psi) op.psi = psi;
    const flags = blockingFlags(row, roster, readers, asRow(row.catalog) ?? catalog);
    if (flags.unknown.length) {
      for (const id of flags.unknown) if (!id.startsWith('aura-owner:')) plan.missing.push(id);
      plan.skipped.push({ actorId, reason: 'status-unknown:' + flags.unknown.join(',') });
    } else {
      const apsTotal = Math.max(0, ec.aps_total === undefined || ec.aps_total === null ? 3 : int(ec.aps_total));
      const before = { aps: int(ec.aps), reaction: int(ec.reaction) };
      const after = { aps: flags.noActions ? before.aps : apsTotal, reaction: flags.noReactions ? before.reaction : 1 };
      if (after.aps !== before.aps || after.reaction !== before.reaction) op.values = { before, after };
    }
    if (op.values || op.ether || op.psi) plan.ops.push(op);
  }
  plan.missing = [...new Set(plan.missing.filter(Boolean))];
  return plan;
}
